import { EmploymentBasicDto, EmpMinDto } from './employees';
import { InvoiceMinDto } from './invoices';
import { MatterListItemMin, MatterVisibilityInfo } from './matters';
import { MinDto, NamedEntityMinDto } from './other';
import { TimecardListItemData } from './timecards';

export interface ExpenseListItem {
    id: number;
    externalId: string | null;
    expenseTypeCode: string;
    currencyTypeCode: string;
    expenseStatusTypeCode: string;
    description: string;
    expenseAmount: number | null;
    expenseDate: string;
    isNonBillable: boolean;
    employeeId: number | null;
    employee: NamedEntityMinDto | null;
    matter: MatterListItemMin | null;
    timecardId: number | null;
    originalCurrencyTypeCode: string | null;
    expenseAmountOriginalCurrency: number | null;
    effortId: number | null;
    invoices: InvoiceMinDto[];
    isEditable: boolean;
    isDeletable: boolean;
    createdAt?: string | Date;
    lastModifiedAt?: string | Date;
    createdByEmployee?: MinDto;
    changedByEmployee?: MinDto;
    /** A draft may also lack its type, currency and date. */
    isDraft?: boolean;
}

export interface ExpenseDetails extends ExpenseListItem {
    timecard: TimecardListItemData;
}

export interface ExpenseDto {
    id?: number;
    externalId?: string;
    description: string;
    isNonBillable?: boolean;
    matter?: (NamedEntityMinDto & { fullName: string }) | null;
    timecardId?: number | null;
    effortId?: number | null;
    employeeId?: number | null;
    employee?: NamedEntityMinDto | null;
    expenseTypeCode?: string;
    expenseTypeName?: string;
    expenseAmount?: string | null;
    expenseAmountOriginalCurrency?: string | null;
    currencyTypeCode?: string;
    originalCurrencyTypeCode?: string;
    expenseDate: string;
    expenseDateUtcTimestamp?: string;
    expenseStatusTypeCode?: string;
    isEditable?: boolean;
    isDeletable?: boolean;
    createdAt?: string | Date;
    lastModifiedAt?: string | Date;
    createdByEmployee?: MinDto;
    changedByEmployee?: MinDto;
}

export interface ExpenseCreateFromTimecardDto {
    id: number;
    description: string;
    isNonBillable?: boolean;
    timecardId?: number;
    employeeId?: number;
    expenseTypeCode?: string;
    expenseAmountOriginalCurrency?: string | null;
    originalCurrencyTypeCode?: string;
    expenseDate: string;
    expenseDateUtcTimestamp?: string;
}

export interface ExpenseFormData extends ExpenseDto {
    currencyTypeCodeLabel?: string;
    matterLabel?: string;
    expenseTypeCodeLabel?: string;
    employeeLabel?: string;
    orderId?: number;
    isEdited?: boolean;
    isCreated?: boolean;
}

export interface ExpenseForm {
    id?: number;
    externalId: string | null;
    expenseType: { label: string; value: string } | null;
    currencyType: { label: string; value: string } | null;
    description: string;
    expenseAmount: string | null;
    expenseDate: string | null;
    isNonBillable: boolean;
    employee: NamedEntityMinDto | null;
    matter: (NamedEntityMinDto & MatterVisibilityInfo & { fullName: string; isNonBillable: boolean }) | null;
    effortId: number | null;
    timecardId: number | null;
    orderId?: number | null;
    createdAt?: string | Date;
    lastModifiedAt?: string | Date;
    createdByEmployee?: MinDto;
    changedByEmployee?: MinDto;
    invoices?: InvoiceMinDto[];
}

export interface ExpenseCreateDto {
    expenseTypeCode: string | null;
    originalCurrencyTypeCode: string | null;
    description: string;
    expenseAmountOriginalCurrency: string | null;
    expenseDate: Date | null;
    expenseDateUtcTimestamp?: string;
    isNonBillable: boolean;
    employeeId: number | null;
    matterId: number | null;
    effortId: number | null;
    timecardId: number | null;
    orderId?: number | null;
}

export interface ExpenseUpdateDto extends ExpenseCreateDto {
    id: number;
}

export interface ExpenseExportListItemDto {
    id: number;
    matter: MatterListItemMin | null;
    invoices: InvoiceMinDto[] | null;
    expenseDate: string;
    employee: EmpMinDto;
    employment: EmploymentBasicDto | null;
    expenseTypeCode: string;
    description: string | null;
    currencyTypeCode: string;
    expenseAmount: string;
    originalCurrencyTypeCode: string;
    expenseAmountOriginalCurrency: string;
    isNonBillable: boolean;
    externalId: string | null;
    isConnectedToEffort: boolean;
}

export interface ExpensesSwitchToMatterDto {
    currentMatterId: number;
    newMatterId: number;
    newMatterAssignmentId: number | null;
    expenseIds: number[];
}

export interface UpdateExpensesBillability {
    matterId: number;
    expenseIds: number[];
    isNonBillable: boolean;
}

// BE serializes BigDecimal as JSON number (no @JsonFormat override); FE forms
// and types work with strings. Coerce at the API boundary so downstream code
// can rely on ExpenseDto's declared `string | null` shape.
export const normalizeExpenseAmounts = (expense: ExpenseDto): ExpenseDto => ({
    ...expense,
    expenseAmount: typeof expense.expenseAmount === 'number' ? String(expense.expenseAmount) : expense.expenseAmount,
    expenseAmountOriginalCurrency:
        typeof expense.expenseAmountOriginalCurrency === 'number'
            ? String(expense.expenseAmountOriginalCurrency)
            : expense.expenseAmountOriginalCurrency,
});

export const mapToExpensesCreateOnTimecardDto = (expenses: ExpenseDto[]): ExpenseCreateFromTimecardDto[] =>
    expenses.map((exp) => ({
        id: exp.id || 0,
        description: exp.description,
        isNonBillable: exp.isNonBillable ?? false,
        timecardId: exp.timecardId || 0,
        employeeId: exp.employeeId || 0,
        expenseTypeCode: exp.expenseTypeCode,
        expenseAmountOriginalCurrency: exp.expenseAmountOriginalCurrency ?? null,
        originalCurrencyTypeCode: exp.originalCurrencyTypeCode,
        expenseDate: exp.expenseDate,
        expenseDateUtcTimestamp: exp.expenseDate ? new Date(`${exp.expenseDate}T00:00:00`).toISOString() : '',
    }));

export type ExpenseMapList = Record<string, number>[];

export enum ExpenseStatusType {
    DRAFT_INVOICE = 'DRAFT_INVOICE',
    INVOICED = 'INVOICED',
    PAID = 'PAID',
    FINISHED = 'FINISHED',
}

export interface ExpenseEmployeeGroup {
    employeeId: number;
    employeeFullName: string;
    // Separate name parts so the grouped view can reorder to last-name-first under LAST_NAME_FIRST.
    employeeFirstName?: string;
    employeeLastName?: string;
    amountsByCurrency: Record<string, number>;
}

export interface ExpenseTypeGroup {
    expenseTypeCode: string;
    amountsByCurrency: Record<string, number>;
    employees: ExpenseEmployeeGroup[];
}

/**
 * Wire shape for /v1/expenses/calculate-totals — the List view's totals row. One figure in the tenant
 * base currency, as the invoices list reports its totals. Only Amount is totalled: Orig. amount is
 * the same money in the currency actually spent, so a second converted figure would differ from the
 * first only by rate drift and rounding.
 *
 * `excludedCurrencies` lists currencies the tenant's rate source doesn't quote (ECB publishes no UAH,
 * for one). Their amounts reach no total, so the row has to disclose them or the figure reads as
 * covering more than it does. The total is null only when nothing converted.
 */
export interface ExpensesTotalsDto {
    baseCurrencyTypeCode: string;
    totalAmountInBaseCurrency: string | null;
    excludedCurrencies: string[];
}

/**
 * Wire shape for /v1/expenses/grouped — conforms to QueryResponse so the table-with-filters
 * renders normally, while exposing the across-the-set per-currency totals to the Tier 1 totals row.
 * totalElements counts underlying expense lines (not the number of expense-type groups), so the
 * "N items" badge reads identically to the List view.
 */
export interface ExpenseGroupedQueryResponse {
    content: ExpenseTypeGroup[];
    totalsByCurrency: Record<string, number>;
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    first: boolean;
    last: boolean;
    empty: boolean;
    numberOfElements: number;
    sort: { empty: boolean; sorted: boolean; unsorted: boolean };
}
